import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { format, isValid } from "date-fns";
import DataTable from "../DataTable";
import ChartLoading from "../ChartLoading";
import TableSearch from "./TableSearch";
import TableExport from "./TableExport";
import ReportSettingAction from "../../../actions/ReportSettingAction";
import { export2txt, exportCSV } from "../../../api/DataApi";
import LayoutAction from "../../../actions/LayoutAction";
import { useQuery } from "@tanstack/react-query";
import { search } from "../../../api/ProjectApi";
import { formatAmount } from "../../../api/DataTypeUtils";
import { ChipAssignees } from "../../../components/AssigneesSelect";
import { truncateText } from "../../../utils/TextUtil";
import { ChipLabels } from "../../../components/LabelsSelect";
import { CircleCheckBig } from "lucide-react";

interface DataTableViewProps {
  tableColumns: { key: string; title: string }[];
  projectId: string;
  objectType: string;
}

const DataTableView: React.FC<DataTableViewProps> = ({ tableColumns, projectId, objectType }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );
  const currency = projectCurrency || "USD";
  const members =
    useSelector((state: any) => state.projectReducer.getIn(["project", "members"])) || fromJS([]);
  const reportData = useSelector((state: any) => state.reportSetting.get("data")) || fromJS({});
  const filters = reportData.get("filters")?.toJS() || {};

  const table = useSelector((state: any) => state.reportSetting.get("table")) || fromJS({});

  const tablePage = table.get("page") || 0;
  const tablePageSize = table.get("pageSize") || 10;
  const pagination = table.get("pagination")?.toJS() || {};
  const tableSearch = table.get("search") || "";
  const rows = table.get("rows") || fromJS([]);
  const loading = !!table.get("loading");

  const allBuckets = useSelector((state: any) => state.bucketReducer.get(objectType)) || fromJS([]);
  const allLabels = useSelector((state: any) => state.labelReducer.get(objectType)) || fromJS([]);

  const rowDatas = useMemo(() => {
    return rows
      .map((item, index) => {
        if (objectType === "ACTIVITY") {
          const assignees = item.getIn(["event", "data", "assignees"])?.toJS() || [];
          const assignedMembers = members.filter((item) =>
            assignees.includes(item.get("accountId"))
          );
          const startDate = item.getIn(["event", "createdAt"])
            ? new Date(item.getIn(["event", "createdAt"]))
            : null;
          return {
            id: index + tablePage * tablePageSize,
            done: item.getIn(["event", "data", "done"]),
            summary: item.getIn(["event", "data", "summary"]),
            assignees: assignedMembers.toJS(),
            item: item.getIn(["event", "data", "item", "label"]),
            createdAt: startDate && isValid(startDate) ? format(startDate, "yyyy-MM-dd HH:mm") : "",
          };
        }
        const assignees = item.getIn(["data", "assignees", "value"])?.toJS() || [];
        const assignedMembers = members.filter((item) => assignees.includes(item.get("accountId")));
        const labels = item.getIn(["data", "labels", "value"])?.toJS() || [];
        const assignedLabels = allLabels
          .filter((item) => labels.includes(item.get("id")))
          .map((item) => fromJS({ ...item.toJS(), displayName: truncateText(item.get("name")) }));
        const bucket =
          allBuckets.find(
            (bucket) => bucket.get("id") === item.getIn(["data", "bucketId", "value"])
          ) || fromJS({});
        const finishDate = item.getIn(["data", "finish", "value"])
          ? new Date(item.getIn(["data", "finish", "value"]))
          : null;
        const startDate = item.getIn(["data", "start", "value"])
          ? new Date(item.getIn(["data", "start", "value"]))
          : null;
        return {
          id: index + tablePage * tablePageSize,
          name: item.getIn(["data", "name", "value"]),
          account: item.getIn(["data", "account", "value", "label"]),
          contact: item.getIn(["data", "contact", "value", "label"]),
          estimatedRevenue: formatAmount(
            i18n.language,
            item.getIn(["data", "estimatedRevenue", "value"]) || 0,
            currency,
            ""
          ),
          status: item.getIn(["data", "status", "value"]),
          assignees: assignedMembers.toJS(),
          labels: assignedLabels.toJS(),
          bucketId: bucket.get("name") || "",
          source: item.getIn(["data", "source", "value"]),
          stage: item.getIn(["data", "stage", "value"]),
          finish: finishDate && isValid(finishDate) ? format(finishDate, "yyyy-MM-dd") : "",
          start: startDate && isValid(startDate) ? format(startDate, "yyyy-MM-dd HH:mm") : "",
        };
      })
      .toJS();
  }, [objectType, rows, members, allLabels, allBuckets, tablePage, tablePageSize, currency]);

  const onTableSearchChange = (value: string) => {
    dispatch(ReportSettingAction.settingTableReport({ search: value, page: 0 }));
  };

  const onTablePageChange = (page: number, size: number) => {
    dispatch(ReportSettingAction.settingTableReport({ page, pageSize: size }));
  };

  const onExportCsv = () => {
    const exportRequest = { filters, types: [objectType] };
    const filename = `${objectType}_${new Date().toISOString()}.csv`;

    exportCSV(projectId, exportRequest).then(({ data }) => {
      export2txt(data, filename);
      dispatch(
        LayoutAction.setNotification({
          title: t("CSV Export"),
          type: "success",
          message: t("Exported successfully"),
        })
      );
    });
  };

  const getValue = (row: any, key: string) => {
    if (key === "assignees") {
      return (
        <ChipAssignees type="INLINE" assignedMembers={fromJS(row.assignees)} onlyAvatar={false} />
      );
    }
    if (key === "labels") {
      return (
        <ChipLabels
          type="INLINE"
          assignedLabels={fromJS(row.labels)}
          showRemove={false}
          onRemove={() => {}}
        />
      );
    }
    if (key === "done") {
      if (row.done) {
        return (
          <div className="inline-flex items-center justify-center">
            <CircleCheckBig className="w-6 text-green-600" />
          </div>
        );
      }
      return (
        <div className="inline-flex items-center justify-center">
          <CircleCheckBig className="w-6 text-gray-400" />
        </div>
      );
    }
    return row[key];
  };

  const { data: pageResult, isFetching } = useQuery({
    queryKey: [
      "reportTable",
      projectId,
      objectType,
      tablePage,
      tablePageSize,
      tableSearch,
      filters,
    ],
    queryFn: async () => {
      const filtersData = { ...filters };
      if (tableSearch && tableSearch.trim()) {
        if (objectType === "ACTIVITY") {
          filtersData.summary = {
            operator: "regex",
            values: [tableSearch],
          };
        } else {
          filtersData.name = {
            operator: "regex",
            values: [tableSearch],
          };
        }
      }
      const { pageResult = {} } = await search({
        projectId,
        page: tablePage,
        pageSize: tablePageSize,
        filters: filtersData,
        view: "GRID",
        types: [objectType],
        fields: [],
        sorts: [],
      } as any);
      return pageResult;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!pageResult) return;
    dispatch(
      ReportSettingAction.settingTableReport({
        rows: pageResult?.content || [],
        pagination: pageResult,
        loading: false,
      })
    );
  }, [pageResult, dispatch]);

  useEffect(() => {
    dispatch(ReportSettingAction.settingTableReport({ loading: !!isFetching }));
  }, [isFetching, dispatch]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      <div className="p-5 mb-5 space-y-5">
        <div className="flex items-center justify-between">
          <TableSearch value={tableSearch} onChange={onTableSearchChange} />
          <TableExport onExport={onExportCsv} />
        </div>
        {loading ? (
          <ChartLoading label="Loading table data..." />
        ) : (
          <DataTable
            columns={tableColumns}
            rows={rowDatas}
            loading={loading}
            pagination={pagination}
            onChangePage={onTablePageChange}
            getValue={getValue}
          />
        )}
      </div>
    </div>
  );
};

export default DataTableView;
